import { CstmPagination } from "components/Common/Common";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingIndicator from "components/shared/LoadingIndicator";
import TableLayout from "components/TableLayout";
import { THERAPIST_DETAIL } from "config/paths";
import { useCallback, useMemo, useState } from "react";
import { generatePath, Link } from "react-router-dom";
import { useGetTherapistsListQuery } from "store/services/therapistProfiles";
import { formatString } from "helpers/common";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { Col, Form, Row } from "react-bootstrap";
import { debounce } from "lodash";

const TherapistListing = ({ selectedTherapists, setSelectedTherapists }) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filter, setFilter] = useState({
    search: "",
    status: "active",
    type: "",
    sort: {
        column: "id",
        direction: "desc",
    },
  });

  const { data, isLoading, isError, isFetching, refetch } = useGetTherapistsListQuery({
    page: currentPage,
    limit: pageSize,
    ...filter,
  });
  
  const totalRecords = data?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const isAllSelected = useMemo(() => (
    data?.records?.length > 0 &&
    data?.records.every((item) => selectedTherapists.includes(item.id))
  ), [data?.records, selectedTherapists])

  const column = useMemo(() => [
      {
        headComponent: () => (
          <div className="d-flex align-items-center gap-10">
            <Form.Check
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ),
        component: (item) => (
          <div className="d-flex align-items-center gap-10">
            <Form.Check
              type="checkbox"
              checked={selectedTherapists.includes(item.id)}
              onChange={() => handleSelectTherapist(item.id)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )
      },
      {
        head: "First Name",
        accessor: "firstname",
        sorter: true,
        component: (item) => (
          <div className="d-flex align-items-center gap-10">
            <h6 className="m-0 fw-sbold">
              <Link
                className="text-dark"
                to={generatePath(THERAPIST_DETAIL, { id: item.id })}
              >
                {item.firstname}
              </Link>
            </h6>
          </div>)
      },
      {
        head: "Last Name",
        accessor: "lastname",
        sorter: true,
        isComponent: true,
        component: (item, ind) => (
          <div className="d-flex align-items-center gap-10">
            <h6 className="m-0 fw-sbold">
              <Link
                className="text-dark"
                to={generatePath(THERAPIST_DETAIL, { id: item.id })}
              >
                {item.lastname}
              </Link>
            </h6>
          </div>
        ),
      },
      {
        head: "Type",
        accessor: "type",
        component: (item, index) => (
          <div className="d-flex align-items-center gap-10">
            {formatString(item?.therapist_detail?.type)}
          </div>
        )
      },
      {
        head: <div className="w-100 d-flex justify-content-center">Discipline</div>,
        accessor: "Discipline",
        isComponent: true,
        component: (item, index) => (
          <p className="text_cap text-center">{item?.therapist_detail?.therapist_discipline?.assign_term}</p>
        )
      },
  ]);

  const handlePageChange = (page) => {
    window.scrollTo(0, 0);
    setCurrentPage(page);
  };

  const handleFilterChange = (key) => (value) => {
    setFilter({ ...filter, [key]: value })
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleSelectTherapist = (id) => {
    setSelectedTherapists((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    const currentIds = data?.records?.map((item) => item.id) || [];
  
    if (e.target.checked) {
      // Add all current page IDs (no duplicates)
      setSelectedTherapists((prev) => Array.from(new Set([...prev, ...currentIds])));
    } else {
      // Remove all current page IDs
      setSelectedTherapists((prev) => prev.filter((id) => !currentIds.includes(id)));
    }
  };

  const handleSearchChange = useCallback(debounce((value) => {
    if (handleFilterChange) {
      handleFilterChange("search")(value);
    }
  }, 500), [handleFilterChange, 500]);

  return (
    <div className="table-outer">
      {isLoading ? (
        <div style={{ height: "200px" }}>
          <LoadingIndicator />
        </div>
      ) : isError ? (
        <ErrorComponent />
      ) : (
        <>
          {(isFetching) && <LoadingOverlay />}
          <Row>
            <Col md="4">
              <div className="iconWithText position-relative h-100">
                <Form.Control
                  type="search"
                  placeholder={"Search"}
                  className="search-input"
                  aria-label="Search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    handleSearchChange(e.target.value);
                  }}
                  style={{ height: "100%", border: 0, fontSize: "14px" }}
                />
              </div>
            </Col>
          </Row>
          <TableLayout
            data={data?.records}
            column={column}
            url={"/manage-team/therapist-profiles/detail"}
            filter={filter}
            handleSort={handleFilterChange("sort")}
          />
        </>
      )}
      <CstmPagination
        totalPages={totalPages}
        activePage={currentPage}
        handlePageChange={handlePageChange}
      />
    </div>
  )
}

export default TherapistListing;